import { Icon } from "./icon";

/**
 * Profile v1 re-skin — tokenized pill-chip group (overline label + a wrapping
 * row of tinted chips). Pure presentational SERVER-SAFE component (no
 * `'use client'`): used by the About card (languages / specialties / service
 * areas / award level / committees) and the Association Involvement card
 * (per-category item chips).
 *
 * Tints are built exclusively from `--brand-*` tokens via the repo's
 * `color-mix` idiom (see `components/home/OpenHouseWeekExplorer.tsx`) — no
 * hardcoded hex. The 1px inset ring is an inline `box-shadow` (deterministic
 * tokenized color; equivalent rendering to Tailwind's inset ring utilities).
 *
 * Returns `null` when `chips` is empty so callers can render unconditionally.
 */

export type ChipTint = "neutral" | "sky" | "success" | "sun" | "primary";

export interface ChipGroupChip {
  text: string;
  /** Optional leading Material Symbols glyph (size 14). */
  icon?: string;
}

/**
 * The tokenized tint recipes — exported so sibling badges (review sentiment,
 * AI theme chips, the association activity badge) reuse the SAME color-mix
 * percentages instead of carrying drifted copies.
 */
export const TINTS: Record<
  ChipTint,
  { background: string; color: string; ring: string }
> = {
  neutral: {
    background: "var(--brand-surface-subtle)",
    color: "var(--brand-text)",
    ring: "color-mix(in srgb, var(--brand-text) 12%, transparent)",
  },
  sky: {
    background: "color-mix(in srgb, var(--brand-sky) 12%, white)",
    color: "color-mix(in srgb, var(--brand-sky) 45%, var(--brand-text))",
    ring: "color-mix(in srgb, var(--brand-sky) 30%, transparent)",
  },
  success: {
    background: "color-mix(in srgb, var(--brand-success) 9%, white)",
    color: "var(--brand-success)",
    ring: "color-mix(in srgb, var(--brand-success) 25%, transparent)",
  },
  sun: {
    background: "color-mix(in srgb, var(--brand-sun) 14%, white)",
    color: "color-mix(in srgb, var(--brand-sun) 45%, var(--brand-text))",
    ring: "color-mix(in srgb, var(--brand-sun) 35%, transparent)",
  },
  primary: {
    background: "color-mix(in srgb, var(--brand-primary) 8%, white)",
    color: "var(--brand-primary-text, var(--brand-primary))",
    ring: "color-mix(in srgb, var(--brand-primary) 25%, transparent)",
  },
};

export function ChipGroup({
  label,
  chips,
  tint,
}: {
  label: string;
  chips: ChipGroupChip[];
  tint: ChipTint;
}) {
  if (chips.length === 0) return null;
  const tintStyle = TINTS[tint];

  return (
    <div>
      <h3
        className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em]"
        style={{ color: "var(--brand-text-muted)" }}
      >
        {label}
      </h3>
      <div className="flex flex-wrap gap-1.5">
        {chips.map((chip, index) => (
          <span
            key={`${chip.text}-${index}`}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12.5px] font-medium"
            style={{
              backgroundColor: tintStyle.background,
              color: tintStyle.color,
              boxShadow: `inset 0 0 0 1px ${tintStyle.ring}`,
            }}
          >
            {chip.icon && <Icon name={chip.icon} size={14} />}
            {chip.text}
          </span>
        ))}
      </div>
    </div>
  );
}
